import * as React from "react";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { DnsRecordDialog } from "./DnsRecordDialog";
import { useDeleteDnsRecord, useDnsZone } from "@/hooks/useDns";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { DnsRecordDto } from "@/types/api";

interface DnsTabProps {
  containerId: number;
  domainName: string;
  hasPackage: boolean;
}

export function DnsTab({ containerId, domainName, hasPackage }: DnsTabProps) {
  const { data, isLoading, error, refetch, isFetching } = useDnsZone(
    hasPackage ? containerId : undefined
  );
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<DnsRecordDto | undefined>();

  function openAdd()  { setEditing(undefined); setDialogOpen(true); }
  function openEdit(r: DnsRecordDto) { setEditing(r); setDialogOpen(true); }

  return (
    <div className="space-y-4">
      <Header
        domainName={domainName}
        nameservers={data?.nameservers ?? []}
        onRefresh={() => void refetch()}
        onAdd={openAdd}
        refreshing={isFetching}
        canEdit={hasPackage}
      />

      {!hasPackage ? (
        <NotProvisioned domainName={domainName} />
      ) : isLoading ? (
        <ListSkeleton />
      ) : error ? (
        <ErrorState error={error} />
      ) : !data || data.records.length === 0 ? (
        <EmptyState onAdd={openAdd} />
      ) : (
        <>
          {/* Desktop ≥ md */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>TTL</TableHead>
                  <TableHead>Pri</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.records.map(r => (
                  <DnsRow key={r.id} record={r} containerId={containerId} onEdit={() => openEdit(r)} />
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile < md */}
          <div className="grid gap-3 md:hidden">
            {data.records.map(r => (
              <DnsCard key={r.id} record={r} containerId={containerId} onEdit={() => openEdit(r)} />
            ))}
          </div>
        </>
      )}

      <DnsRecordDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        containerId={containerId}
        domainName={domainName}
        initial={editing}
      />
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────
function Header({
  domainName, nameservers, onRefresh, onAdd, refreshing, canEdit
}: {
  domainName: string; nameservers: string[]; onRefresh: () => void; onAdd: () => void;
  refreshing: boolean; canEdit: boolean;
}) {
  const usingHosterDash = nameservers.some(ns => /\.20i\.com|hosterdash/i.test(ns));
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h2 className="font-display text-lg font-semibold">DNS records</h2>
        <p className="text-sm text-muted-foreground">
          Live zone for <span className="font-mono">{domainName}</span>.
        </p>
        {nameservers.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={usingHosterDash ? "success" : "outline"}>
              {usingHosterDash ? "Pointed at HosterDash" : "External nameservers"}
            </Badge>
            <span className="font-mono text-muted-foreground">{nameservers.join(", ")}</span>
          </div>
        )}
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4" />
            Add record
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Desktop row ────────────────────────────────────────────
function DnsRow({ record, containerId, onEdit }: { record: DnsRecordDto; containerId: number; onEdit: () => void }) {
  return (
    <TableRow>
      <TableCell>
        <Badge variant="outline" className="font-mono uppercase">{record.type}</Badge>
      </TableCell>
      <TableCell className="font-mono text-sm">{record.name}</TableCell>
      <TableCell className="max-w-md truncate font-mono text-xs text-muted-foreground">{record.value}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{record.ttl}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{record.priority ?? "—"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit record">
            <Pencil className="h-4 w-4" />
          </Button>
          <DeleteRecordButton record={record} containerId={containerId} />
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Mobile card ────────────────────────────────────────────
function DnsCard({ record, containerId, onEdit }: { record: DnsRecordDto; containerId: number; onEdit: () => void }) {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Badge variant="outline" className="font-mono uppercase">{record.type}</Badge>
            <span className="truncate font-mono text-sm">{record.name}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit record">
              <Pencil className="h-4 w-4" />
            </Button>
            <DeleteRecordButton record={record} containerId={containerId} />
          </div>
        </div>
        <p className="truncate font-mono text-xs text-muted-foreground">{record.value}</p>
        <p className="text-[11px] text-muted-foreground">
          TTL {record.ttl}s{record.priority !== null && record.priority !== undefined ? ` · pri ${record.priority}` : ""}
        </p>
      </CardContent>
    </Card>
  );
}

function DeleteRecordButton({ record, containerId }: { record: DnsRecordDto; containerId: number }) {
  const del = useDeleteDnsRecord(containerId);
  const { toast } = useToast();
  async function onDelete() {
    try {
      await del.mutateAsync(record.id);
      toast({ title: "Record removed", variant: "success" });
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Delete record">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this record?</AlertDialogTitle>
          <AlertDialogDescription>
            DNS propagation can take up to TTL ({record.ttl}s) — recipients caching the old value will keep
            seeing it until expiry.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >Delete record</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── States ────────────────────────────────────────────────
function NotProvisioned({ domainName }: { domainName: string }) {
  return (
    <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
      {domainName} doesn't have a 20i hosting package yet, so we can't manage DNS for it.
      Finish the order on HostShop first — the webhook will tag this domain automatically.
    </CardContent></Card>
  );
}

function ListSkeleton() {
  return (
    <Card><CardContent className="space-y-2 py-5">
      {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
    </CardContent></Card>
  );
}

function ErrorState({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError ? `${error.message} (${error.code ?? error.status})` : "Network error.";
  return (
    <Card><CardContent className="py-8 text-center text-sm text-destructive">
      Couldn't load DNS: {message}
    </CardContent></Card>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <Card><CardContent className="flex flex-col items-center gap-3 py-10 text-center">
      <p className="text-sm text-muted-foreground">
        No records on this zone yet. Add one to start routing traffic.
      </p>
      <Button onClick={onAdd}><Plus className="h-4 w-4" />Add record</Button>
    </CardContent></Card>
  );
}
